import { Fragment, useContext, useEffect, useMemo, useRef, useState, type CSSProperties, type ReactNode } from 'react';
import type { InspectionTemplateBody, LayoutBody } from '@ayumia/contracts';
import {
  aggregateScope,
  aggregateTextScope,
  answerAt,
  blockComponent,
  blocksOn,
  confirmationApplies,
  displayText,
  effectiveTemplate,
  evaluateFormCondition,
  formAggregates,
  formPages,
  hiddenPageSections,
  layoutOf,
  layoutWarnings,
  navigationMode,
  openForm,
  openNewForm,
  pageMissing,
  pageOfPath,
  fieldSpan,
  placementOf,
  reactionStates,
  readonlyHolds,
  reconcileAnswers,
  seriesAsTable,
  validatePage,
  visiblePages,
  type AnswerOrigins,
  type FormEntryState,
  type ReactionMessage,
  type ReactionOutcome,
  type FormFormat,
  type FormPage,
  type LayoutBlock,
  type NavigationMode,
} from '@ayumia/component-runtime';
import { text } from '../app/i18n';
import { clearLeaveGuard, setLeaveGuard, type LeaveGuard } from '../app/leave-guard';
import { EvidenceInput } from '../views/EvidenceInput';
import { CameraEvidence, PHOTO_MAX_DEFAULT, type CameraDevice } from './CameraEvidence';
import { shortId, useReferenceLabels } from '../views/reference-labels';
import { AppServicesContext, useAppServices } from '../views/ViewContext';
import { collectDeviations, formatNumber, withComputed, type Answers, type ConformityRules, type Deviation, type Entry, type Inputs, type Section } from './form-targets';
import { collectWarnings, needsServerWarnings, warningsByPath, type FormWarning } from './form-warnings';
import { titleAnswersKey } from './work-item-title';
import { NonconformityDialog } from './NonconformityDialog';
import { missingItems, missingText } from './missing-items';
import { SeriesSection, type SeriesRow } from './SeriesSection';
import { SeriesLedger } from './SeriesLedger';
import { duplicateGroups, ledgerSummary, stepsOf, type SummaryItem } from './series-ledger';
import { civilToday, type CivilTimeZones } from './civil-date';
import { CivilTodayContext, ConformityRulesContext, FormWorkItemContext, TemplateEntry } from './TemplateEntry';
import { CivilTimeZoneContext } from './widgets/DateTimeInput';
import { civilTimeZone } from './widgets/zoned-time';
import { isDisplayOnlyEntry } from './widgets/rich-text';
import type { DraftStore } from '../views/line-draft-store';
import { draftOrigins, type FormDraftValues } from './form-draft';
import { FormReactionsContext, type FormReactionsView } from './form-reactions-context';
import './form-reactions.css';
import { AggregateList, ConfirmationDialog, ExitDialog, FormActionBar, FormBanner, ObjectBanner, ReadonlyScope, RegionLayout, UnsupportedBlock, aggregateShown, blockRegion, sectionRegion, useFormFormat } from './FormLayoutParts';
import { FormHeader } from './form-v2/FormHeader';
import { ActionButtonBlock, CounterChipBlock, FormImageBlock, type FormButtonGestures } from './form-v2/DisplayBlocks';
import { ListDetailList, ListDetailPanel } from './form-v2/ListDetail';
import { StripSection } from './form-v2/StripSection';
import { readLastPerson } from './form-v2/last-person';
import { FormPeopleContext, isPersonEntry, personDefaultOf, PersonEchoProvider, type FormPeopleSource, type TemplateRef } from './form-v2/PersonField';
import { footerHidden, headerSettings, inStrip, isListDetail, keptFields, presentationOf, rowIsEmpty, submitShortcutShown, usesFormV2 } from './form-v2/presentation';
import './form-v2/form-v2.css';
import './form-console.css';
const NO_TITLE_FIELDS: readonly string[] = [];
const NO_RULES: ConformityRules = {};

type RepeatAnswer = Answers & { item_id: string; children?: Answers[] };

/** Champs d'un groupe (élément, sous-ligne) sur ses propres `$answers`, champs calculés compris. */
function GroupFields({ entries, values, onChange, disabled, errorPrefix, errors, idPrefix, inputs, warnings = {} }: { entries: readonly Entry[]; values: Answers; onChange: (id: string, value: unknown) => void; disabled: boolean; errorPrefix: string; errors: Readonly<Record<string, string>>; idPrefix: string; inputs: Inputs; warnings?: Readonly<Record<string, readonly string[]>> }) {
  const scope = withComputed(entries, values, inputs);
  return (
    <>
      {entries
        .filter((entry) => entry.compute !== undefined || evaluateFormCondition(entry.visible_when, values))
        .map((entry) => {
          const id = entry.field.field_id;
          const required = entry.field.required === true || (entry.required_when !== undefined && evaluateFormCondition(entry.required_when, values));
          const message = errors[`${errorPrefix}.${id}`];
          const warning = warnings[`${errorPrefix}.${id}`];
          return <TemplateEntry key={id} entry={entry} values={scope} inputs={inputs} controlId={`${idPrefix}-${id}`} required={required} disabled={disabled} onChange={(value) => onChange(id, value)} onFill={(filled) => Object.entries(filled).forEach(([key, next]) => onChange(key, next))} {...(message === undefined ? {} : { error: message })} {...(warning === undefined ? {} : { warnings: warning })} />;
        })}
    </>
  );
}

/**
 * Section répétée (inspection-template 1.1.0, ADR-125 §6) : une carte par objet de la liste que la tâche a
 * reçue (les zones d'un plan), ses champs, puis une ligne par sous-objet lu dans le modèle (les équipements
 * de la zone). La réponse est la liste `{ item_id, …, children }` que le serveur relit contre le gabarit.
 */
function RepeatedSection({ section, itemIds, value, onChange, disabled, errors, inputs, warnings = {} }: { section: Section & { repeat: NonNullable<Section['repeat']> }; itemIds: readonly string[]; value: RepeatAnswer[]; onChange: (next: RepeatAnswer[]) => void; disabled: boolean; errors: Readonly<Record<string, string>>; inputs: Inputs; warnings?: Readonly<Record<string, readonly string[]>> }) {
  const services = useAppServices();
  const labels = useReferenceLabels(services.client, [{ type: section.repeat.item_type, ids: [...itemIds] }]);
  const children = section.repeat.children;
  const childKey = children?.child_key ?? 'child_id';
  const [childRows, setChildRows] = useState<Record<string, Array<{ id: string; label: string }>>>({});
  useEffect(() => {
    if (children === undefined) return;
    let alive = true;
    void Promise.all(itemIds.map(async (itemId) => {
      const filter = { ...((children.filter ?? {}) as Record<string, unknown>), [children.link_field]: { eq: itemId } } as never;
      const rows = (await services.client.listObjects<Record<string, unknown>>(children.object_type, { filter, limit: 200 })).data;
      return [itemId, rows.map((row) => ({ id: String(row['id']), label: String(row['name'] ?? row['display_name'] ?? row['code'] ?? shortId(String(row['id']))) }))] as const;
    })).then((entries) => {
      if (alive) setChildRows(Object.fromEntries(entries));
    });
    return () => {
      alive = false;
    };
  }, [services.client, children, itemIds.join(',')]);
  const answerOf = (itemId: string): RepeatAnswer => value.find((entry) => entry.item_id === itemId) ?? { item_id: itemId };
  const update = (itemId: string, patch: (current: RepeatAnswer) => RepeatAnswer): void => onChange(itemIds.map((id) => (id === itemId ? patch(answerOf(id)) : answerOf(id))));
  return (
    <div className="ay-form__repeat">
      {itemIds.map((itemId, index) => {
        const answer = answerOf(itemId);
        const own = Object.fromEntries(Object.entries(answer).filter(([key]) => key !== 'item_id' && key !== 'children'));
        return (
          <fieldset key={itemId} className="ay-form__section ay-form__item">
            <legend>{labels.get(itemId) ?? shortId(itemId)}</legend>
            <GroupFields entries={section.fields} values={own} inputs={inputs} disabled={disabled} errors={errors} warnings={warnings} errorPrefix={`answers.${section.section_id}.${index}`} idPrefix={`${section.section_id}-${index}`} onChange={(id, next) => update(itemId, (current) => ({ ...current, [id]: next }))} />
            {children !== undefined && (childRows[itemId] ?? []).length > 0 && (
              <div className="ay-form__children" aria-label={text(children.label, 'fr', 'Sous-lignes')}>
                {(childRows[itemId] ?? []).map((child, position) => {
                  const current = (answer.children ?? []).find((entry) => entry[childKey] === child.id) ?? {};
                  const values = Object.fromEntries(Object.entries(current).filter(([key]) => key !== childKey));
                  return (
                    <div key={child.id} className="ay-form__child">
                      <span className="ay-form__child-label">{child.label}</span>
                      <GroupFields entries={children.fields} values={values} inputs={inputs} disabled={disabled} errors={errors} errorPrefix={`answers.${section.section_id}.${index}.children.${position}`} idPrefix={`${section.section_id}-${index}-${child.id}`} onChange={(id, next) => update(itemId, (item) => {
                        const list = (childRows[itemId] ?? []).map((row) => (item.children ?? []).find((entry) => entry[childKey] === row.id) ?? { [childKey]: row.id });
                        return { ...item, children: list.map((entry) => (entry[childKey] === child.id ? { ...entry, [id]: next } : entry)) };
                      })} />
                    </div>
                  );
                })}
              </div>
            )}
          </fieldset>
        );
      })}
      {errors[`answers.${section.section_id}`] !== undefined && <span className="ay-field__error">{errors[`answers.${section.section_id}`]}</span>}
    </div>
  );
}

/**
 * Formulaire de gabarit (`inspection_template`, Core ; ADR-058 §2, ADR-064 §5, ADR-141 §3 bis) :
 * sections, champs, visibilité et obligations conditionnelles, preuves exigées, cibles et champs
 * calculés, séries ouvertes, évalués sur `$answers` par l'évaluateur sûr. Le même parcours que la
 * tablette : à la validation, les écarts constatés ouvrent la question de non-conformité. Aide de
 * saisie seulement : le serveur relit le gabarit à sa version exacte et refuse un formulaire
 * incomplet, champ par champ.
 */

export interface TemplateSubmission {
  answers: Record<string, unknown>;
  evidence: Record<string, string[]>;
  /** Chemins des réponses pour lesquelles l'opérateur ouvre une non-conformité (ADR-141 §1.3). */
  nonconformities?: string[];
  /** Réponses posées par une réaction et non modifiées (complete-work-item 1.1.0, ADR-146 addendum (17)) ; le serveur les rejoue. */
  answer_origins?: AnswerOrigins;
}

/** État de saisie et ce que les réactions demandent d'afficher (messages, confirmation en attente). */
interface ReactionEntry extends FormEntryState {
  messages: ReactionMessage[];
  confirm?: ReactionMessage;
  cancel?: FormEntryState;
}

const entryOf = (outcome: ReactionOutcome): ReactionEntry => ({ answers: outcome.answers, origins: outcome.origins, messages: outcome.messages, ...(outcome.confirm === undefined || outcome.cancel === undefined ? {} : { confirm: outcome.confirm, cancel: outcome.cancel }) });

/** Texte d'un message de réaction : la langue de l'écran, sinon le libellé de la réaction. */
function reactionText(message: ReactionMessage): string {
  return message.text['fr'] ?? Object.values(message.text)[0] ?? text(message.label, 'fr', '');
}

type SeriesSectionType = Section & { series: NonNullable<Section['series']> };

/** Origines des réponses encore présentes (une marque sur une réponse vide ne part pas). */
function keptOrigins<T>(origins: Readonly<Record<string, T>>, answers: Answers): Record<string, T> {
  return Object.fromEntries(
    Object.entries(origins).filter(([path]) => {
      const value = answerAt(answers, path);
      return value !== undefined && value !== null && value !== '';
    }),
  );
}

/**
 * Une série commence avec le nombre de lignes minimal qu'elle attend. `lastPerson` : la dernière personne choisie
 * sur ce navigateur pour un champ « Personne » à `person_default: last` (ADR-152 §3), une proposition seulement.
 */
function initialAnswers(template: InspectionTemplateBody, lastPerson: (fieldId: string) => string | undefined = () => undefined): Answers {
  const initial: Answers = {};
  for (const section of template.sections) {
    // Un registre (1.6.0) commence vide : les lignes s'ajoutent au panneau. Sans plafond, le minimum seul.
    if (section.series !== undefined) initial[section.section_id] = seriesAsTable(template, section) ? [] : Array.from({ length: Math.min(section.series.min_count ?? 0, section.series.max_count ?? Number.MAX_SAFE_INTEGER) }, () => ({}));
    // Valeur par défaut déclarée d'un champ de la fiche (`field.default`, « Dépôt » d'office comme le v1), comme la tablette.
    else if (section.repeat === undefined) {
      for (const entry of section.fields) {
        const fallback = (entry.field as { default?: unknown }).default;
        if (fallback !== undefined && fallback !== null && entry.compute === undefined && !isDisplayOnlyEntry(entry)) initial[entry.field.field_id] = fallback;
        // « Valeur au départ » fixe (inspection-template-body 1.13.0), sur un champ resté vide, comme la tablette.
        const start = (entry as { initial_value?: { source: string; value?: unknown } }).initial_value;
        if (start?.source === 'fixed' && initial[entry.field.field_id] === undefined && entry.compute === undefined) initial[entry.field.field_id] = start.value;
        // Champ « Personne » (ADR-152 §3) : `last`, la dernière personne choisie ici ; `site` attend T2 (rien) ; `none`, rien.
        if (isPersonEntry(entry) && personDefaultOf(entry) === 'last' && initial[entry.field.field_id] === undefined) {
          const last = lastPerson(entry.field.field_id);
          if (last !== undefined) initial[entry.field.field_id] = last;
        }
      }
    }
  }
  return initial;
}

/** Lignes d'une série telles qu'elles partent : ni champ calculé (le serveur calcule), ni valeur vide. */
function seriesRows(section: SeriesSectionType, rows: SeriesRow[]): SeriesRow[] {
  const computed = new Set(section.fields.filter((entry) => entry.compute !== undefined).map((entry) => entry.field.field_id));
  return rows.map((row) => Object.fromEntries(Object.entries(row).filter(([key, value]) => !computed.has(key) && value !== null && value !== undefined)));
}

/** Plafond de photos d'un champ : `evidence.max_count` s'il est déclaré, sinon {@link PHOTO_MAX_DEFAULT}. */
function evidenceMax(evidence: { min_count: number }): number {
  const declared = (evidence as { max_count?: unknown }).max_count;
  const max = typeof declared === 'number' && Number.isInteger(declared) && declared > 0 ? declared : PHOTO_MAX_DEFAULT;
  return Math.max(max, evidence.min_count);
}

function evidenceLabel(kind: string | undefined): string {
  return kind === 'signature' ? 'Signature' : kind === 'photo' ? 'Photo' : 'Preuve';
}

/** « Champ obligatoire. » → « champ obligatoire » : le message suit le libellé du champ. */
function lowerFirst(message: string): string {
  const trimmed = message.endsWith('.') ? message.slice(0, -1) : message;
  return trimmed === '' ? trimmed : `${trimmed[0]!.toLowerCase()}${trimmed.slice(1)}`;
}

/** « 02 et 08 », « 02, 05 et 08 ». */
function listLines(lines: readonly number[]): string {
  const shown = lines.map((line) => String(line).padStart(2, '0'));
  return shown.length < 2 ? shown.join('') : `${shown.slice(0, -1).join(', ')} et ${shown.at(-1)!}`;
}

/** Un agrégat de la synthèse : le nombre de lignes en grand, une somme, le compte par valeur, les badges. */
function SummaryEntry({ item }: { item: SummaryItem }) {
  if (item.kind === 'count')
    return (
      <div className="ay-summary__count" data-testid="summary-count">
        <span className="ay-summary__big">{item.value}</span>
        <span className="ay-summary__muted">{item.label}</span>
      </div>
    );
  if (item.kind === 'sum')
    return (
      <div data-testid={`summary-sum`}>
        <span className="ay-summary__muted">{item.label} · </span>
        <strong className="ay-ledger__num">
          {formatNumber(item.value)}
          {item.unit === undefined ? '' : ` ${item.unit}`}
        </strong>
      </div>
    );
  if (item.kind === 'count_by') {
    // Toujours montré, sous le libellé du champ ; les lignes sans valeur se comptent à part (comme la tablette).
    const by = item.by ?? [];
    const missing = item.value - by.reduce((sum, entry) => sum + entry.count, 0);
    return (
      <div data-testid="summary-count-by-block">
        <span className="ay-summary__by-title">{item.label}</span>
        <ul className="ay-summary__by" aria-label={item.label} data-testid="summary-count-by">
          {by.map((entry) => (
            <li key={entry.value}>
              <span>{entry.label}</span>
              <strong className="ay-ledger__num">{entry.count}</strong>
            </li>
          ))}
          {missing > 0 && (
            <li className="ay-summary__by-missing" data-testid="summary-by-missing">
              <span>Non renseigné</span>
              <strong className="ay-ledger__num">{missing}</strong>
            </li>
          )}
          {item.value === 0 && <li className="ay-summary__muted">—</li>}
        </ul>
      </div>
    );
  }
  return (
    <span className={`ay-badge ay-badge--${item.kind === 'nonconform' ? 'danger' : 'warning'}`} data-testid={`summary-${item.kind}`}>
      {item.label} {item.value}
    </span>
  );
}

/** Taille de cible de la densité « gants » quand la recette n'en fixe pas (inspection-template-body 1.8.0, `density`). */
const GLOVES_TARGET_PX = 56;

/** Pied par défaut d'un assistant (§2.6) : Précédent, progression, Suivant puis Valider sur la dernière page. */
const DEFAULT_FOOTER = { start: 'previous', center: 'progress', end: 'next' } as const;

function noChange(): void {
  // Lecture seule (`readonly_when`) : la saisie est ignorée, le champ reste lisible.
}

export function TemplateForm({
  template: declared,
  subject,
  disabled,
  errors: givenErrors,
  onSubmit,
  inputs = {},
  timeZones = {},
  workItemId,
  conformityRules = NO_RULES,
  camera,
  layout: layoutBody,
  designRecipe,
  context,
  format: forcedFormat,
  sheetDate,
  operator,
  draftStore,
  draftKey,
  onSaveDraft,
  onLeave,
  titleFields = NO_TITLE_FIELDS,
  preview = false,
  revealHidden = false,
  templateRef,
}: {
  template: InspectionTemplateBody;
  subject: { type: string; id: string } | undefined;
  disabled: boolean;
  errors: Readonly<Record<string, string>>;
  onSubmit: (submission: TemplateSubmission) => void;
  /** Entrées de la tâche : listes des sections répétées (`repeat.items_input`), `$inputs` des bornes et calculs. */
  inputs?: Readonly<Record<string, unknown>>;
  /** Fuseaux de la date du jour qui borne le calendrier : le site, sinon le tenant ; aucun : pas de bornes (jamais UTC). */
  timeZones?: CivilTimeZones;
  /** ADR-147 §3 : réponses citées par le titre calculé de la tâche ; leur changement le fait recalculer au serveur. */
  titleFields?: readonly string[];
  /** Tâche du formulaire : le sélecteur d'objet borne ses listes à l'entité de la tâche (`work_item_id`). */
  workItemId?: string;
  /** Bornes des règles partagées citées par le gabarit, servies par GetWorkItem (`conformity_rules`) : la cible affichée est celle que le serveur revérifie. */
  conformityRules?: ConformityRules;
  /** Caméra des preuves photo (doublure en test) ; par défaut celle du navigateur. */
  camera?: CameraDevice;
  /** Gabarit de mise en page servi par GetWorkItem (`layout.body`, ADR-146 §3.2) ; absent : repli en une colonne. */
  layout?: LayoutBody;
  /** Recette effective du poste (`design_recipe`) : densité et taille de cible ; rien ne l'affiche. */
  designRecipe?: { density?: string; min_target_px?: number; hidden_regions?: readonly string[] };
  /** `$context` des liaisons, en lecture seule (servi par le serveur). */
  context?: Readonly<Record<string, unknown>>;
  /** Format imposé (aperçu, test) ; par défaut `web`, ou `mobile` sous 600 px. */
  format?: FormFormat;
  /** Date de la fiche (`context.task.sheet_date`, AAAA-MM-JJ), montrée dans le bandeau sans conversion. */
  sheetDate?: string;
  /** Nom de la personne connectée, montré dans le bandeau. */
  operator?: string;
  /** Copie locale du brouillon de la fiche (« Enregistrer et sortir ») : tampon hors ligne du brouillon du serveur. */
  draftStore?: DraftStore;
  draftKey?: string;
  /** Brouillon de référence au serveur (contrat work-item-draft) : la fiche ne se quitte qu'une fois accepté. */
  onSaveDraft?: (values: FormDraftValues) => Promise<boolean>;
  /** Sortie de la fiche (« Sortir ») ; par défaut, la boîte de tâches. */
  onLeave?: () => void;
  /**
   * Aperçu d'un éditeur (Studio) : ni garde de sortie, ni confirmation du navigateur, ni « Sortir » au bandeau ;
   * la fiche n'est pas une saisie réelle (ADR-146 addendum (10)).
   */
  preview?: boolean;
  /**
   * Éditeur seulement (ADR-146 addendum (15)) : les champs et parties masqués par leur condition restent
   * dessinés, en pointillés, pour être choisis ; ils ne sont jamais des réponses (rien ne part d'un aperçu).
   */
  revealHidden?: boolean;
  /**
   * Gabarit cité (ADR-152 §5) : hors tâche (aperçu, lancement d'une action, banc), les personnes d'un champ
   * « Personne » se lisent par le gabarit ; il nomme aussi la « dernière personne choisie » de l'appareil.
   */
  templateRef?: TemplateRef;
}) {
  const today = civilToday(new Date(), timeZones);
  const timeZone = civilTimeZone(timeZones);
  // Personnes des champs « Personne » (ADR-152) : par la tâche, sinon par le gabarit ; l'aperçu montre des exemples.
  const peopleKey = templateRef?.artifact_id ?? draftKey ?? text(declared.label, 'fr', 'formulaire');
  const peopleSource = useMemo<FormPeopleSource>(
    () => ({ workItemId, template: templateRef, on: sheetDate ?? today ?? undefined, preview, templateKey: peopleKey }),
    [workItemId, templateRef?.artifact_id, templateRef?.artifact_version, sheetDate, today, preview, peopleKey],
  );
  // Brouillon local repris à la réouverture : réponses, preuves et page courante.
  const [restored] = useState(() => (draftKey === undefined ? null : (draftStore?.load(draftKey) ?? null)));
  const restoredValues = (restored?.values ?? {}) as { answers?: Answers; evidence?: Record<string, string[]>; page_id?: string; answer_origins?: unknown };
  // Réactions (inspection-template-body 1.10.0, ADR-146 addendum (17)) : chaque changement passe par l'interprète du Core,
  // le même qu'à la tablette et au serveur ; l'état porte les origines des réponses suggérées. Le gabarit effectif compile
  // les réactions dérivées (afficher, masquer, obligatoire, lecture seule, choix, pages) dans les conditions du gabarit.
  // Un brouillon repris garde ses origines (work-item-draft 1.1.0, ADR-146 addendum (21)) : une suggestion reste une suggestion.
  // Une fiche neuve : ses lignes ouvertes d'office sont des ajouts de ligne (`row_add`).
  const [entry, setEntry] = useState<ReactionEntry>(() => {
    const answers = restoredValues.answers;
    if (answers === undefined) return entryOf(openNewForm(declared, initialAnswers(declared, (fieldId) => (preview ? undefined : readLastPerson(peopleKey, fieldId))), { inputs }));
    return entryOf(openForm(declared, { answers, origins: keptOrigins(draftOrigins(restoredValues.answer_origins), answers) }, { inputs }));
  });
  const answers = entry.answers;
  const setAnswers = (update: (current: Answers) => Answers): void => setEntry((current) => entryOf(reconcileAnswers(declared, current, update(current.answers), { inputs })));
  const template = useMemo(() => effectiveTemplate(declared, answers, inputs), [declared, answers, inputs]);
  // États dérivés par ligne (choix et filtre rechargés par sa propre ligne) et origines : vus par les champs (addendum (21)).
  const reactionView = useMemo<FormReactionsView>(() => ({ origins: entry.origins, states: reactionStates(declared, answers, inputs) }), [declared, answers, inputs, entry.origins]);
  // Validation d'une page qui attend une confirmation : ce qui suit l'accord.
  const afterConfirm = useRef<(() => void) | null>(null);
  /** Réactions de la validation d'une page ; faux si une confirmation est demandée (la suite attend la réponse). */
  const pageReactions = (target: string | undefined, then: () => void): boolean => {
    const outcome = validatePage(declared, entry, target, { inputs });
    // Rien à montrer ni à changer : l'état reste le même objet (le message « À compléter » n'est pas effacé).
    if (outcome.suggested.length > 0 || outcome.cleared.length > 0 || outcome.messages.length > 0 || outcome.confirm !== undefined) setEntry(entryOf(outcome));
    if (outcome.confirm === undefined) return true;
    afterConfirm.current = then;
    return false;
  };
  const [evidence, setEvidence] = useState<Record<string, string[]>>(() => restoredValues.evidence ?? {});
  const [exiting, setExiting] = useState(false);
  // Manques d'une page relevés au poste (« Suivant », onglets) : une aide, le serveur revalide tout.
  const [localErrors, setLocalErrors] = useState<Readonly<Record<string, string>>>({});
  // « À compléter : … » avec « Voir » (comme l'écran v1), au refus de « Suivant » ou de « Valider ».
  const [toast, setToast] = useState<{ text: string; path: string } | null>(null);
  // Une réponse donnée : le message a servi.
  useEffect(() => setToast(null), [answers]);
  // Une erreur de champ disparaît dès que sa réponse change vers une valeur (et sa carte de la synthèse) ;
  // le serveur reste juge à la validation suivante. Même règle que la tablette.
  const errorAt = useRef<{ source: Readonly<Record<string, string>>; local: Readonly<Record<string, string>>; at: Map<string, string> }>({ source: givenErrors, local: localErrors, at: new Map() });
  if (errorAt.current.source !== givenErrors) errorAt.current = { source: givenErrors, local: localErrors, at: new Map() };
  else if (errorAt.current.local !== localErrors) {
    for (const path of Object.keys(localErrors)) errorAt.current.at.delete(path);
    errorAt.current.local = localErrors;
  }
  const valueAt = (path: string): unknown => {
    const [root, first, ...rest] = path.split('.');
    if (root === 'evidence') return evidence[first ?? ''];
    let value: unknown = answers[first ?? ''];
    for (const part of rest) value = /^\d+$/.test(part) ? (Array.isArray(value) ? value[Number(part)] : undefined) : value !== null && typeof value === 'object' ? (value as Record<string, unknown>)[part] : undefined;
    return value;
  };
  const errors: Readonly<Record<string, string>> = Object.fromEntries(
    Object.entries({ ...localErrors, ...givenErrors }).filter(([path]) => {
      const value = valueAt(path);
      const now = JSON.stringify(value ?? null);
      if (!errorAt.current.at.has(path)) errorAt.current.at.set(path, now);
      const present = value !== undefined && value !== null && value !== '' && !(Array.isArray(value) && value.length === 0);
      return !(errorAt.current.at.get(path) !== now && present);
    }),
  );
  const [pending, setPending] = useState<{ submission: TemplateSubmission; deviations: Deviation[] } | null>(null);
  const [confirming, setConfirming] = useState<TemplateSubmission | null>(null);
  const [opening, setOpening] = useState<{ section: string; index: number; nonce: number } | undefined>(undefined);
  const [folds, setFolds] = useState<Readonly<Record<string, boolean>>>({});
  // Ligne en cours de chaque liste et détail (1.10.0), partagée par la liste et sa carte de détail.
  const [cursors, setCursors] = useState<Readonly<Record<string, number>>>({});
  const v2 = usesFormV2(template);

  // Couche de mise en page (1.8.0) ou étapes de 1.6.0 : pages, mode de navigation par format.
  const formLayout = layoutOf(template);
  const layered = formLayout !== undefined;
  const paged = layered || stepsOf(template).length > 0;
  const format = useFormFormat(forcedFormat);
  const mode: NavigationMode = navigationMode(template, format);
  const stagedView = paged && mode === 'steps_with_summary';
  const [pageId, setPageId] = useState<string | undefined>(restoredValues.page_id);

  const reveal = preview && revealHidden;
  const plainEntries = template.sections.filter((section) => section.repeat === undefined && section.series === undefined).flatMap((section): Entry[] => [...section.fields]);
  const plainValues = withComputed(plainEntries, answers, inputs);
  const setAnswer = (key: string, value: unknown): void => setAnswers((current) => ({ ...current, [key]: value }));

  /** Réponses telles qu'elles partent : champs visibles seulement, ni champ calculé ni valeur vide ; rien d'une page cachée. */
  const keptOf = (current: Answers): { kept: Answers; visible: Set<string> } => {
    // Seules les réponses des champs visibles partent : un champ masqué n'est pas une réponse, un champ calculé est calculé par le serveur.
    const visible = new Set<string>();
    const hidden = hiddenPageSections(template, current, inputs as Answers);
    for (const section of template.sections) {
      if (hidden.has(section.section_id) || !evaluateFormCondition(section.visible_when, current)) continue;
      if (section.repeat !== undefined || section.series !== undefined) {
        visible.add(section.section_id);
        continue;
      }
      for (const entry of section.fields) if (entry.compute === undefined && !isDisplayOnlyEntry(entry) && evaluateFormCondition(entry.visible_when, current)) visible.add(entry.field.field_id);
    }
    const kept = Object.fromEntries(Object.entries(current).filter(([key, value]) => visible.has(key) && value !== null && value !== undefined));
    for (const section of template.sections) {
      if (section.series !== undefined && Array.isArray(kept[section.section_id])) kept[section.section_id] = seriesRows(section as SeriesSectionType, kept[section.section_id] as SeriesRow[]);
      // Liste et détail (1.10.0) : la ligne nouvelle ouverte d'office, restée vide, ne part pas.
      if (isListDetail(template, section) && Array.isArray(kept[section.section_id])) kept[section.section_id] = (kept[section.section_id] as SeriesRow[]).filter((row) => !rowIsEmpty(row, keptFields(section)));
    }
    return { kept, visible };
  };

  // Avertissements (addendum d'ADR-126) : ceux de la fiche, calculés ici comme au serveur et à la tablette,
  // et ceux de la couche de mise en page (§2.7) ; « déjà enregistré », demandé au serveur (PreviewFormWarnings)
  // quand le gabarit le déclare. Jamais bloquants. Sans services (aperçu, test) : seuls ceux de la fiche.
  const services = useContext(AppServicesContext);
  // Garde de sortie : un lien du menu ou le retour du navigateur ouvre « Sortir » au lieu de quitter ;
  // un rechargement ou la fermeture de l'onglet demande confirmation au navigateur.
  const guarded = !preview && (onSaveDraft !== undefined || draftKey !== undefined);
  const proceed = useRef<(() => void) | null>(null);
  useEffect(() => {
    if (!guarded) return undefined;
    const guard: LeaveGuard = (next) => {
      proceed.current = next;
      setExiting(true);
    };
    const onUnload = (event: BeforeUnloadEvent): void => event.preventDefault();
    setLeaveGuard(guard);
    window.addEventListener('beforeunload', onUnload);
    return () => {
      clearLeaveGuard(guard);
      window.removeEventListener('beforeunload', onUnload);
    };
  }, [guarded]);
  const leave = (): void => {
    setLeaveGuard(null);
    const next = proceed.current;
    proceed.current = null;
    if (next !== null) next();
    else if (onLeave !== undefined) onLeave();
    else services?.navigate({ kind: 'inbox' });
  };
  const sent = keptOf(answers).kept;
  const local: FormWarning[] = [...collectWarnings(template, sent), ...layoutWarnings(template, sent)];
  const [remote, setRemote] = useState<FormWarning[]>([]);
  const remoteKey = services !== null && workItemId !== undefined && needsServerWarnings(template) ? JSON.stringify(sent) : null;
  useEffect(() => {
    if (remoteKey === null || workItemId === undefined || services === null) return;
    let alive = true;
    const timer = setTimeout(() => {
      services.client.queryPost<{ warnings: FormWarning[] }>('PreviewFormWarnings', { work_item_id: workItemId, answers: remoteKey }).then(
        (envelope) => {
          if (alive) setRemote(envelope.data.warnings.filter((warning) => warning.code === 'FORM_DUPLICATE_EXISTING'));
        },
        () => {
          // Hors ligne ou refusé : pas d'avertissement « déjà enregistré », la saisie continue.
          if (alive) setRemote([]);
        },
      );
    }, 500);
    return () => {
      alive = false;
      clearTimeout(timer);
    };
  }, [services, workItemId, remoteKey]);
  // Titre calculé (ADR-147 §3) : les réponses citées partent à RefreshWorkItemTitle quand elles changent ; un refus est sans effet.
  const titleKey = services !== null && workItemId !== undefined ? titleAnswersKey(titleFields, answers) : null;
  useEffect(() => {
    if (titleKey === null || workItemId === undefined || services === null) return;
    const timer = setTimeout(() => {
      services.client.command('RefreshWorkItemTitle', { payload: { answers: JSON.parse(titleKey) as Record<string, unknown> }, target: { type: 'WorkItem', id: workItemId } }).catch(() => undefined);
    }, 500);
    return () => clearTimeout(timer);
  }, [services, workItemId, titleKey]);
  const allWarnings = [...local, ...remote];
  const warnings = warningsByPath(allWarnings);
  const aggregates = formAggregates(template, sent);

  const renderSection = (section: Section): ReactNode => {
    // Section en lecture seule (`placements.<id>.readonly_when`) : toute la section, présentation seulement.
    const sectionReadonly = readonlyHolds(placementOf(template, section.section_id).readonly_when, { answers });
    const node = section.repeat !== undefined ? (
              <div key={section.section_id}>
                <h3 className="ay-form__repeat-title">{text(section.label, 'fr', section.section_id)}</h3>
                <RepeatedSection
                  section={section as Section & { repeat: NonNullable<Section['repeat']> }}
                  itemIds={Array.isArray(inputs[section.repeat.items_input]) ? (inputs[section.repeat.items_input] as unknown[]).map(String) : []}
                  value={(answers[section.section_id] as RepeatAnswer[] | undefined) ?? []}
                  onChange={(next) => (sectionReadonly ? noChange() : setAnswer(section.section_id, next))}
                  disabled={disabled}
                  errors={errors}
                  inputs={inputs}
                  warnings={warnings}
                />
              </div>
            ) : section.series !== undefined ? (
              <div key={section.section_id}>
                <h3 className="ay-form__repeat-title">{text(section.label, 'fr', section.section_id)}</h3>
                {seriesAsTable(template, section) ? (
                  <SeriesLedger section={section as SeriesSectionType} rows={(answers[section.section_id] as SeriesRow[] | undefined) ?? []} onChange={(next) => (sectionReadonly ? noChange() : setAnswer(section.section_id, next))} disabled={disabled} errors={errors} inputs={inputs} warnings={warnings} form={sent} open={opening?.section === section.section_id ? opening : undefined} />
                ) : (
                  <SeriesSection section={section as SeriesSectionType} rows={(answers[section.section_id] as SeriesRow[] | undefined) ?? []} onChange={(next) => (sectionReadonly ? noChange() : setAnswer(section.section_id, next))} disabled={disabled} errors={errors} inputs={inputs} warnings={warnings} />
                )}
              </div>
            ) : (
              <fieldset key={section.section_id} className="ay-form__section">
                <legend>{text(section.label, 'fr', section.section_id)}</legend>
                {section.fields
                  .filter((entry) => reveal || entry.compute !== undefined || evaluateFormCondition(entry.visible_when, answers))
                  .map((entry) => {
                    const masked = reveal && entry.compute === undefined && !evaluateFormCondition(entry.visible_when, answers);
                    const id = entry.field.field_id;
                    const required = entry.field.required === true || (entry.required_when !== undefined && evaluateFormCondition(entry.required_when, answers));
                    const proofNeeded = entry.evidence !== undefined && evaluateFormCondition(entry.evidence.when, answers);
                    const message = errors[`answers.${id}`];
                    const warning = warnings[`answers.${id}`];
                    // Champ en lecture seule (`readonly_when`, 1.8.0) : lisible et focalisable, jamais `disabled`.
                    const readonly = sectionReadonly || readonlyHolds((entry as { readonly_when?: unknown }).readonly_when, { answers });
                    // Champ « photo » (`presentation.widget: photo`) : le champ n'est que sa prise de vue ; ni case de
                    // saisie vide ni second libellé sous le titre de la partie (« Photos » une seule fois).
                    if (presentationOf(entry).widget === 'photo' && entry.evidence !== undefined && entry.evidence.kind === 'photo') {
                      const label = text(entry.field.label, 'fr', id);
                      const repeated = label === text(section.label, 'fr', section.section_id);
                      const shownLabel = repeated && entry.evidence.min_count === 0 ? null : `${label}${entry.evidence.min_count > 1 ? ` (au moins ${entry.evidence.min_count})` : ''}`;
                      return (
                        <div key={id} className="ay-form__cell" style={{ gridColumn: `span ${fieldSpan(template, section.section_id, entry, format)}` }}>
                          <div className="ay-field" data-testid={`photo-field-${id}`}>
                            {shownLabel !== null && (
                              <span className="ay-field__label">
                                {shownLabel}
                                {entry.evidence.min_count >= 1 && <span aria-hidden="true"> *</span>}
                              </span>
                            )}
                            {proofNeeded && (
                              <CameraEvidence
                                id={`evidence-${id}`}
                                label={`Photos : ${label}`}
                                value={evidence[id] ?? []}
                                onChange={(ids) => (readonly ? noChange() : setEvidence((current) => ({ ...current, [id]: ids })))}
                                disabled={disabled || readonly}
                                subject={subject}
                                maxCount={evidenceMax(entry.evidence)}
                                {...(camera === undefined ? {} : { camera })}
                              />
                            )}
                            {errors[`evidence.${id}`] !== undefined && <span className="ay-field__error">{errors[`evidence.${id}`]}</span>}
                          </div>
                        </div>
                      );
                    }
                    const control = (
                      <TemplateEntry
                        key={id}
                        entry={entry}
                        values={plainValues}
                        inputs={inputs}
                        controlId={id}
                        required={required}
                        disabled={disabled}
                        onChange={(value) => (readonly ? noChange() : setAnswer(id, value))}
                        onFill={(filled) => (readonly ? noChange() : setAnswers((current) => ({ ...current, ...filled })))}
                        path={`answers.${id}`}
                        {...(message === undefined ? {} : { error: message })}
                        {...(warning === undefined ? {} : { warnings: warning })}
                        {...(subject === undefined ? {} : { subject })}
                      >
                        {proofNeeded && entry.evidence!.kind === 'photo' && (
                          <div className="ay-field">
                            <span className="ay-field__label">
                              {evidenceLabel('photo')}
                              {entry.evidence!.min_count === 0 ? ' (facultative)' : entry.evidence!.min_count > 1 ? `s (au moins ${entry.evidence!.min_count})` : ''}
                              {entry.evidence!.min_count >= 1 && <span aria-hidden="true"> *</span>}
                            </span>
                            <CameraEvidence
                              id={`evidence-${id}`}
                              label={`Photos : ${text(entry.field.label, 'fr', id)}`}
                              value={evidence[id] ?? []}
                              onChange={(ids) => setEvidence((current) => ({ ...current, [id]: ids }))}
                              disabled={disabled}
                              subject={subject}
                              maxCount={evidenceMax(entry.evidence!)}
                              {...(camera === undefined ? {} : { camera })}
                            />
                            {errors[`evidence.${id}`] !== undefined && <span className="ay-field__error">{errors[`evidence.${id}`]}</span>}
                          </div>
                        )}
                        {proofNeeded && entry.evidence!.kind !== 'photo' && (
                          <div className="ay-field">
                            <label className="ay-field__label" htmlFor={`evidence-${id}`}>
                              {/* 1.3.0 : min_count 0 = preuve proposée mais facultative, sans astérisque (ADR-141, addendum (3)). */}
                              {evidenceLabel(entry.evidence!.kind)}
                              {entry.evidence!.min_count === 0 ? ' (facultative)' : entry.evidence!.min_count > 1 ? `s (au moins ${entry.evidence!.min_count})` : ''}
                              {entry.evidence!.min_count >= 1 && <span aria-hidden="true"> *</span>}
                            </label>
                            <EvidenceInput id={`evidence-${id}`} value={evidence[id] ?? []} onChange={(ids) => setEvidence((current) => ({ ...current, [id]: ids }))} disabled={disabled} subject={subject} />
                            {errors[`evidence.${id}`] !== undefined && <span className="ay-field__error">{errors[`evidence.${id}`]}</span>}
                          </div>
                        )}
                      </TemplateEntry>
                    );
                    const shown = readonly ? (
                      <ReadonlyScope key={id} readonly>
                        {control}
                      </ReadonlyScope>
                    ) : (
                      control
                    );
                    // Grille de 12 colonnes dans chaque partie (ADR-146 addendum (26)) : largeur déclarée, sinon 12 / colonnes ; 12 au téléphone.
                    return (
                      <div key={id} className="ay-form__cell" style={{ gridColumn: `span ${fieldSpan(template, section.section_id, entry, format)}` }}>
                        {masked ? (
                          <div className="ay-form__masked" data-masked="true" title="Visible sous condition">
                            {shown}
                          </div>
                        ) : (
                          shown
                        )}
                      </div>
                    );
                  })}
              </fieldset>
            );
    // Une série ou une répétition en lecture seule : tout le bloc ; une section simple l'est champ par champ.
    return sectionReadonly && (section.series !== undefined || section.repeat !== undefined) ? (
      <ReadonlyScope key={section.section_id} readonly>
        {node}
      </ReadonlyScope>
    ) : (
      node
    );
  };

  // Fiche en étapes (inspection-template-body 1.6.0, addendum d'ADR-141 du 2026-10-19) : une étape à la fois,
  // la synthèse des séries en registre toujours visible, avertissements et erreurs qui mènent à leur ligne,
  // « Valider » dans la synthèse à la dernière étape. Présentation : le serveur relit la fiche entière.
  // Généralisée aux pages de la couche de mise en page (1.8.0) et à ses quatre modes de navigation.
  const sectionOfPath = (path: string): { section: string; index?: number } | undefined => {
    const [, first, second] = path.split('.');
    if (first === undefined) return undefined;
    const grouped = template.sections.find((section) => section.section_id === first && (section.series !== undefined || section.repeat !== undefined));
    if (grouped !== undefined) return { section: grouped.section_id, ...(second !== undefined && /^\d+$/.test(second) ? { index: Number(second) } : {}) };
    const owner = template.sections.find((section) => section.fields.some((entry) => entry.field.field_id === first));
    return owner === undefined ? undefined : { section: owner.section_id };
  };

  const declaredPages = visiblePages(template, answers, inputs as Answers);
  const pages: FormPage[] = declaredPages.length > 0 ? declaredPages : formPages(template).slice(0, 1);
  const missingOf = (page: FormPage): string[] => pageMissing(template, page, answers, evidence, inputs as Answers);
  // Page courante : celle qu'on a choisie ; cachée depuis, la première page incomplète.
  const pageIndex = ((): number => {
    if (pageId === undefined) return 0;
    const found = pages.findIndex((page) => page.page_id === pageId);
    if (found >= 0) return found;
    return Math.max(0, pages.findIndex((page) => missingOf(page).length > 0));
  })();
  const page = pages[pageIndex]!;
  const lastStep = pageIndex >= pages.length - 1;

  /** Message d'un manque relevé au poste : le champ obligatoire, ou le nombre de lignes d'une série. */
  const missingMessage = (path: string): string => {
    const [, first, second] = path.split('.');
    const series = second === undefined ? template.sections.find((section) => section.section_id === first && section.series !== undefined) : undefined;
    if (series !== undefined) {
      const min = Math.max(1, series.series?.min_count ?? 1);
      return min > 1 ? `Ajoutez au moins ${min} lignes.` : 'Ajoutez au moins une ligne.';
    }
    return 'Champ obligatoire.';
  };
  const focusPath = (path: string): void => {
    setTimeout(() => {
      // Le champ de premier niveau : son contrôle prend le focus et vient sous les yeux.
      const control = document.getElementById(`input-${path.split('.')[1] ?? ''}`) ?? document.getElementById(`evidence-${path.split('.')[1] ?? ''}`);
      control?.focus();
      control?.scrollIntoView?.({ block: 'center' });
    }, 0);
  };
  /** Montre les manques d'une page sous ses champs ; vrai s'il n'en reste pas. */
  const showMissing = (target: FormPage): boolean => {
    const missing = missingOf(target);
    setLocalErrors(Object.fromEntries(missing.map((path) => [path, missingMessage(path)])));
    if (missing.length > 0) {
      // Une section repliée qui contient un manque se rouvre.
      setFolds((current) => {
        const next = { ...current };
        for (const path of missing) {
          const owner = sectionOfPath(path)?.section;
          if (owner !== undefined) next[owner] = true;
        }
        return next;
      });
      focusPath(missing[0]!);
    }
    return missing.length === 0;
  };
  const openPage = (index: number): void => {
    const target = pages[index];
    if (target !== undefined) setPageId(target.page_id);
  };
  /** Annonce des manques : « À compléter : … », « Voir » mène au premier. */
  const announce = (paths: readonly string[]): void => {
    if (paths.length === 0) return;
    setToast({ text: missingText(missingItems(template.sections, paths)), path: paths[0]! });
  };
  /**
   * « Suivant » (décision de l'utilisateur du 2026-10-19, toute fiche en pages, assistant comme étapes avec
   * synthèse, couche déclarée ou étapes de 1.6.0) : sans `allow_skip`, la page doit être complète ; sinon on
   * reste, le premier manque mis en évidence et annoncé. Le serveur revalide tout.
   */
  const nextPage = (): void => {
    if (lastStep) return;
    // Réactions de la page (« validation de page ») d'abord ; après une confirmation acceptée, on ne les rejoue pas.
    if (afterConfirm.current === null && !pageReactions(page.page_id, nextPage)) return;
    afterConfirm.current = null;
    if (page.allow_skip !== true && !showMissing(page)) {
      announce(missingOf(page));
      return;
    }
    setLocalErrors({});
    setToast(null);
    openPage(pageIndex + 1);
  };
  const previousPage = (): void => openPage(Math.max(0, pageIndex - 1));
  /** Le fil : revenir en arrière toujours ; en avant, jamais par-dessus une page incomplète. */
  const choosePage = (index: number): void => {
    if (index <= pageIndex || mode === 'tabs') {
      openPage(index);
      return;
    }
    for (let at = pageIndex; at < index; at++) {
      const crossed = pages[at]!;
      if (crossed.allow_skip === true || missingOf(crossed).length === 0) continue;
      openPage(at);
      showMissing(crossed);
      announce(missingOf(crossed));
      return;
    }
    setLocalErrors({});
    setToast(null);
    openPage(index);
  };

  const goTo = (path: string): void => {
    const target = sectionOfPath(path);
    if (target === undefined) return;
    const owner = pageOfPath(template, path);
    if (owner !== undefined && pages.some((candidate) => candidate.page_id === owner)) setPageId(owner);
    if (target.index !== undefined) setOpening({ section: target.section, index: target.index, nonce: Date.now() });
    else focusPath(path);
  };
  const lineOf = (path: string): string => {
    const target = sectionOfPath(path);
    return target?.index === undefined ? '' : `Ligne ${String(target.index + 1).padStart(2, '0')} · `;
  };
  const ledgers = template.sections.filter((section) => section.series !== undefined && seriesAsTable(template, section));
  const summary = ledgers.flatMap((section) => ledgerSummary(section, (answers[section.section_id] as Answers[] | undefined) ?? []));
  const errorPaths = Object.keys(errors).filter((path) => path.startsWith('answers.') || path.startsWith('evidence.'));
  /** Libellé d'un chemin d'erreur : le champ (ou la section d'une série) qu'il désigne. */
  const labelOfPath = (path: string): string => {
    const [, first, , field] = path.split('.');
    const grouped = template.sections.find((section) => section.section_id === first && (section.series !== undefined || section.repeat !== undefined));
    if (grouped !== undefined) {
      const entry = field === undefined ? undefined : grouped.fields.find((candidate) => candidate.field.field_id === field);
      return entry === undefined ? text(grouped.label, 'fr', grouped.section_id) : text(entry.field.label, 'fr', field!);
    }
    const entry = template.sections.flatMap((section) => section.fields).find((candidate) => candidate.field.field_id === first);
    return entry === undefined ? (first ?? path) : text(entry.field.label, 'fr', first!);
  };
  /** Compteur d'une page : l'agrégat déclaré (`counter`) ; pour les étapes de 1.6.0, le nombre de lignes du registre. */
  const pageCounter = (target: FormPage): string | undefined => {
    if (layered) {
      const value = target.counter === undefined ? undefined : aggregates.find((candidate) => candidate.aggregate_id === target.counter!.aggregate_ref);
      return value === undefined ? undefined : aggregateShown(value);
    }
    const ledger = ledgers.find((section) => target.sections.includes(section.section_id));
    return ledger === undefined ? undefined : String(((answers[ledger.section_id] as unknown[] | undefined) ?? []).length);
  };
  const pageLabel = (target: FormPage): string => text(target.label, 'fr', target.page_id);
  const stepLabel = (index: number): string => {
    const count = pageCounter(pages[index]!);
    return `${pageLabel(pages[index]!)}${count === undefined ? '' : ` · ${count}`}`;
  };
  // Sur téléphone (ADR-146 addendum (15)) : étapes compactes, la courante en « 2/3 Pièces », les autres en pastille numérotée.
  const compact = format === 'mobile';
  const trail = (
    <nav className={`ay-staged__steps${compact ? ' ay-staged__steps--compact' : ''}`} aria-label="Étapes">
      {pages.map((target, index) => {
        const done = index < pageIndex && (!layered || missingOf(target).length === 0);
        const current = index === pageIndex;
        return (
          <span key={target.page_id} className="ay-staged__step-wrap">
            {index > 0 && !compact && <span className="ay-staged__sep" aria-hidden="true" />}
            <button type="button" className={`ay-staged__step${done ? ' ay-staged__step--done' : ''}`} aria-current={current ? 'step' : undefined} onClick={() => choosePage(index)}>
              <span className="ay-staged__step-num">{compact && current ? `${index + 1}/${pages.length}` : done ? '✓' : index + 1}</span>
              <span className={`ay-staged__step-label${compact && !current ? ' ay-visually-hidden' : ''}`}>{compact ? pageLabel(target) : stepLabel(index)}</span>
            </button>
          </span>
        );
      })}
    </nav>
  );

  /** Synthèse (`FormSummary`, ou celle des étapes de 1.6.0) : agrégats, doublons, avertissements, « À corriger », Valider. */
  const summaryItems = (block: LayoutBlock | undefined): ReactNode[] => {
    const aggregatePath = (block?.bind as Readonly<Record<string, { path: string }>> | undefined)?.['aggregates']?.path;
    const declared = aggregatePath === undefined ? [] : aggregatePath.startsWith('$aggregates.') ? aggregates.filter((value) => value.aggregate_id === aggregatePath.split('.')[1]) : aggregates;
    return [
      ...summary.map((item, index) => <SummaryEntry key={`${item.kind}-${index}`} item={item} />),
      <AggregateList key="aggregates" values={declared} />,
      ...duplicateGroups(template.sections, answers, allWarnings).map((group) => (
        <button key={`dup:${group.path}`} type="button" className="ay-summary__alert" onClick={() => goTo(group.path)}>
          <strong>Doublon</strong>
          <span>
            {group.label} {group.value} : lignes {listLines(group.lines)}
          </span>
        </button>
      )),
      ...allWarnings.filter((warning) => warning.code !== 'FORM_DUPLICATE_IN_SERIES').map((warning) => (
        <button key={`${warning.field}:${warning.code}:${warning.message}`} type="button" className="ay-summary__alert" onClick={() => goTo(warning.field)}>
          <strong>{warning.code === 'FORM_WARNING' ? 'Avertissement' : 'Doublon'}</strong>
          <span>
            {lineOf(warning.field)}
            {warning.message}
          </span>
        </button>
      )),
      ...errorPaths.map((path) => (
        <button key={path} type="button" className="ay-summary__alert ay-summary__alert--error" onClick={() => goTo(path)}>
          <strong>À corriger</strong>
          <span>
            {lineOf(path)}
            {labelOfPath(path)} : {lowerFirst(errors[path]!)}
          </span>
        </button>
      )),
    ];
  };

  // ---- Couche de mise en page : sections placées, blocs, régions.
  const bindingScope = { answers, inputs, aggregates: aggregateScope(aggregates), form: { ...sent, warnings: allWarnings }, context: context ?? {} };
  const blockVisible = (block: LayoutBlock): boolean => block.visible_when === undefined || readonlyHolds(block.visible_when, { answers, inputs, aggregates: aggregateScope(aggregates) });
  /**
   * Gestes d'un bouton d'action (`ActionButton`, ADR-146 addendum (20)) : ceux des boutons du formulaire,
   * rien d'autre. Une ligne ajoutée passe par la saisie (réactions « ajout de ligne » comprises).
   */
  const buttonGestures: FormButtonGestures = {
    next: paged && !lastStep ? () => nextPage() : undefined,
    previous: paged && pageIndex > 0 ? () => previousPage() : undefined,
    submit: () => submit(),
    addRow: (sectionId: string) => {
      const target = template.sections.find((candidate) => candidate.section_id === sectionId);
      if (target?.series === undefined) return;
      const rows = (answers[sectionId] as Answers[] | undefined) ?? [];
      const max = (target.series as { max_count?: number }).max_count;
      if (max !== undefined && rows.length >= max) return;
      setAnswer(sectionId, [...rows, {}]);
      setCursors((current) => ({ ...current, [sectionId]: rows.length }));
    },
    openPage: (pageId: string) => {
      const index = pages.findIndex((candidate) => candidate.page_id === pageId);
      if (index >= 0) choosePage(index);
    },
  };
  const renderBlock = (block: LayoutBlock, inAside: boolean): ReactNode => {
    const name = blockComponent(block).name;
    if (name === 'ObjectBanner') return <ObjectBanner key={block.block_id} block={block} template={template} scope={bindingScope} />;
    if (name === 'FormSummary') {
      const items = summaryItems(block);
      if (inAside) return <Fragment key={block.block_id}>{items}</Fragment>;
      return (
        <section key={block.block_id} className="ay-layout__summary" aria-label="Synthèse" data-testid={`block-${block.block_id}`}>
          {items}
        </section>
      );
    }
    if (name === 'FormImage') return <FormImageBlock key={block.block_id} block={block} />;
    if (name === 'CounterChip') return <CounterChipBlock key={block.block_id} block={block} aggregates={aggregates} />;
    if (name === 'ActionButton') return <ActionButtonBlock key={block.block_id} block={block} gestures={buttonGestures} disabled={disabled} />;
    return <UnsupportedBlock key={block.block_id} block={block} />;
  };
  const sectionHasError = (sectionId: string): boolean => Object.keys(errors).some((path) => sectionOfPath(path)?.section === sectionId);
  const placedSection = (section: Section): ReactNode => {
    const placement = placementOf(template, section.section_id);
    const columns = format === 'mobile' ? 1 : format === 'portrait' ? (placement.columns?.portrait ?? 1) : (placement.columns?.landscape ?? 1);
    const classes = [
      'ay-placed',
      reveal && !evaluateFormCondition(section.visible_when, answers) ? 'ay-form__masked' : '',
      // En carte par défaut, comme la tablette (`template_form_section.dart`) ; `card: false` la retire. Le rendu 1.10 a ses propres cartes.
      placement.card === true || (!v2 && placement.card !== false) ? 'ay-placed--card' : '',
      columns > 1 ? `ay-placed--cols-${columns}` : '',
      placement.emphasis !== undefined && placement.emphasis !== 'none' ? `ay-placed--${placement.emphasis}` : '',
      placement.size !== undefined && placement.size !== 'normal' ? `ay-placed--${placement.size}` : '',
    ].filter((name) => name !== '');
    const content = renderSection(section);
    if (placement.collapsible === 'open' || placement.collapsible === 'closed') {
      const open = sectionHasError(section.section_id) || (folds[section.section_id] ?? placement.collapsible === 'open');
      return (
        <details
          key={section.section_id}
          className={classes.join(' ')}
          data-section={section.section_id}
          open={open}
          onToggle={(event) => {
            const now = (event.currentTarget as HTMLDetailsElement).open;
            setFolds((current) => (current[section.section_id] === now ? current : { ...current, [section.section_id]: now }));
          }}
        >
          <summary className="ay-placed__toggle">{text(section.label, 'fr', section.section_id)}</summary>
          {content}
        </details>
      );
    }
    return (
      <div key={section.section_id} className={classes.join(' ')} data-section={section.section_id}>
        {content}
      </div>
    );
  };
  const summaryRegions = new Set((layoutBody?.regions ?? []).filter((region) => region.role === 'summary').map((region) => region.region_id));
  const headerRegions = new Set((layoutBody?.regions ?? []).filter((region) => region.role === 'header').map((region) => region.region_id));
  const stripRegions = new Set((layoutBody?.regions ?? []).filter((region) => region.role === 'strip').map((region) => region.region_id));
  /** Un bloc de l'en-tête : sa région a le rôle `header` ; sans gabarit servi, la région déclarée `header`. */
  const inHeader = (block: LayoutBlock): boolean => {
    const region = blockRegion(layoutBody, block.region);
    return region === undefined ? block.region === 'header' : headerRegions.has(region);
  };
  // Régions masquées par la recette du poste (par rôle) ; le bandeau (`header`) ne se masque jamais.
  const hiddenRoles = new Set((designRecipe?.hidden_regions ?? []).filter((role) => role !== 'header'));
  /** Contenu des régions pour des pages : sections visibles à leur région, blocs à la leur ; un bloc persistant une fois. */
  const compose = (shown: readonly FormPage[], titled: boolean) => {
    const contents = new Map<string, ReactNode[]>();
    const fallback: ReactNode[] = [];
    const aside: ReactNode[] = [];
    const top: ReactNode[] = [];
    const seen = new Set<string>();
    const push = (region: string | undefined, node: ReactNode): void => {
      if (region === undefined) fallback.push(node);
      else contents.set(region, [...(contents.get(region) ?? []), node]);
    };
    for (const target of shown) {
      for (const block of blocksOn(template, target.page_id)) {
        if (seen.has(block.block_id) || !blockVisible(block)) continue;
        seen.add(block.block_id);
        const region = blockRegion(layoutBody, block.region);
        const isSummary = blockComponent(block).name === 'FormSummary';
        // La région d'en-tête est portée par le bandeau du formulaire : ses blocs y vont.
        if (inHeader(block)) top.push(renderBlock(block, false));
        else if (stagedView && (region === undefined ? isSummary : summaryRegions.has(region))) aside.push(renderBlock(block, true));
        else push(region, renderBlock(block, false));
      }
      let titledIn: string | undefined | null = null;
      for (const section of template.sections) {
        if (!target.sections.includes(section.section_id) || (!reveal && !evaluateFormCondition(section.visible_when, answers))) continue;
        const region = sectionRegion(layoutBody, placementOf(template, section.section_id).region);
        if (titled && titledIn === null) {
          titledIn = region;
          push(region, (
            <h2 key={`title-${target.page_id}`} className="ay-layout__page-title">
              {pageLabel(target)}
            </h2>
          ));
        }
        if (isListDetail(template, section)) {
          // Liste dans sa région, détail de la ligne en cours dans celle du panneau (`entry_panel.region`).
          const series = section as SeriesSectionType;
          const rows = (answers[section.section_id] as SeriesRow[] | undefined) ?? [];
          const props = {
            template,
            section: series,
            answers,
            rows,
            cursor: cursors[section.section_id] ?? Math.max(0, rows.length - 1),
            setCursor: (index: number) => setCursors((current) => ({ ...current, [section.section_id]: index })),
            onChange: (next: SeriesRow[]) => setAnswer(section.section_id, next),
            disabled,
            errors,
            warnings,
            inputs,
          };
          push(region, <ListDetailList key={`${section.section_id}-list`} {...props} />);
          const panelRegion = sectionRegion(layoutBody, placementOf(template, section.section_id).entry_panel?.region) ?? region;
          push(panelRegion, <ListDetailPanel key={`${section.section_id}-detail`} {...props} />);
          continue;
        }
        if (region !== undefined && inStrip(template, section, stripRegions)) {
          push(region, <StripSection key={section.section_id} section={section} answers={answers} inputs={inputs} disabled={disabled} errors={errors} setAnswer={setAnswer} evidence={evidence} setEvidence={(id, ids) => setEvidence((current) => ({ ...current, [id]: ids }))} subject={subject} />);
          continue;
        }
        push(region, placedSection(section));
      }
    }
    return { contents, fallback, aside, top };
  };
  const regions = (shown: readonly FormPage[], titled: boolean) => {
    const composed = compose(shown, titled);
    return { ...composed, node: <RegionLayout body={layoutBody} format={format} contents={composed.contents} fallback={composed.fallback} hiddenRoles={hiddenRoles} /> };
  };

  // ---- Corps selon le mode. Le bandeau porte les blocs de l'en-tête des pages affichées.
  let body: ReactNode = null;
  let bannerBlocks: ReactNode[] = [];
  let actionCenter: ReactNode | undefined;
  if (stagedView) {
    const composed = layered ? regions([page], false) : null;
    const aside = composed === null || composed.aside.length === 0 ? summaryItems(undefined) : composed.aside;
    bannerBlocks = composed?.top ?? [];
    body = (
      <div className="ay-staged">
        {trail}
        <div className="ay-staged__body">
          {!hiddenRoles.has('summary') && (
            <aside className="ay-summary" aria-label="Synthèse">
              {aside}
            </aside>
          )}
          <div className="ay-staged__content">
            {composed === null ? template.sections.filter((section) => page.sections.includes(section.section_id) && (reveal || evaluateFormCondition(section.visible_when, answers))).map(placedSection) : composed.node}
          </div>
        </div>
      </div>
    );
  } else if (layered && mode === 'tabs') {
    const current = regions([page], false);
    bannerBlocks = current.top;
    body = (
      <>
        <div className="ay-tabs" role="tablist" aria-label="Pages">
          {pages.map((target, index) => {
            const missing = missingOf(target).length;
            return (
              <button key={target.page_id} type="button" role="tab" id={`tab-${target.page_id}`} aria-controls={`tabpanel-${target.page_id}`} aria-selected={index === pageIndex} className="ay-tabs__tab" onClick={() => openPage(index)}>
                <span>{pageLabel(target)}</span>
                {missing > 0 && (
                  <span className="ay-badge ay-badge--warning" data-testid={`tab-missing-${target.page_id}`}>
                    {missing} à compléter
                  </span>
                )}
              </button>
            );
          })}
        </div>
        <div role="tabpanel" id={`tabpanel-${page.page_id}`} aria-labelledby={`tab-${page.page_id}`} className="ay-tabs__panel">
          {current.node}
        </div>
      </>
    );
  } else if (layered && mode === 'wizard') {
    const footer = { ...DEFAULT_FOOTER, ...(layoutBody?.footer ?? {}), ...(formLayout.footer ?? {}) } as Readonly<Record<'start' | 'center' | 'end', string>>;
    // Pied de l'assistant : la barre commune (Précédent, position, Suivant ou Valider la fiche) ; un bloc
    // déclaré au centre du pied y prend la place de la position.
    const declared = (formLayout.blocks ?? []).find((candidate) => candidate.block_id === footer.center);
    if (declared !== undefined) actionCenter = renderBlock(declared, false);
    const current = regions([page], false);
    bannerBlocks = current.top;
    body = (
      <div className="ay-wizard">
        {trail}
        {current.node}
      </div>
    );
  } else if (layered) {
    const all = regions(pages, pages.length > 1);
    bannerBlocks = all.top;
    body = all.node;
  }

  const send = (submission: TemplateSubmission): void => {
    if (confirmationApplies(template, submission.answers)) setConfirming(submission);
    else onSubmit(submission);
  };
  const submit = (): void => {
    if (afterConfirm.current === null && !pageReactions(paged ? page.page_id : undefined, submitAfterReactions)) return;
    submitAfterReactions();
  };
  const submitAfterReactions = (): void => {
    afterConfirm.current = null;
    // Fiche en pages : la première page qui a un manque s'ouvre, le manque est annoncé, rien ne part
    // (le serveur revalide de toute façon).
    if (paged) {
      const first = pages.findIndex((target) => missingOf(target).length > 0);
      if (first >= 0) {
        openPage(first);
        showMissing(pages[first]!);
        announce(missingOf(pages[first]!));
        return;
      }
    }
    setToast(null);
    if (Object.keys(localErrors).length > 0) setLocalErrors({});
    const { kept, visible } = keptOf(answers);
    const proofs = Object.fromEntries(Object.entries(evidence).filter(([key, ids]) => visible.has(key) && ids.length > 0));
    // Origines des réponses suggérées encore envoyées (complete-work-item 1.1.0) ; le serveur les rejoue.
    const origins = keptOrigins(entry.origins, kept);
    const submission: TemplateSubmission = { answers: kept, evidence: proofs, ...(Object.keys(origins).length === 0 ? {} : { answer_origins: origins }) };
    const deviations = collectDeviations(template, kept, inputs, conformityRules);
    if (deviations.length === 0) send(submission);
    else setPending({ submission, deviations });
  };
  const confirmation = formLayout?.confirmation;
  // Fiche parcourue page à page (étapes, onglets, assistant) : la barre du bas mène d'une page à l'autre.
  const navigated = pages.length > 1 && (stagedView || (layered && (mode === 'tabs' || mode === 'wizard')));
  const targetMin = designRecipe?.min_target_px ?? (designRecipe?.density === 'gloves' ? GLOVES_TARGET_PX : undefined);

  return (
    <FormReactionsContext.Provider value={reactionView}>
    <ConformityRulesContext.Provider value={conformityRules}>
    <FormWorkItemContext.Provider value={workItemId}>
    <FormPeopleContext.Provider value={peopleSource}>
    <PersonEchoProvider sections={template.sections} answers={answers}>
      <CivilTodayContext.Provider value={today}>
      <CivilTimeZoneContext.Provider value={timeZone}>
        <form
          className={v2 ? 'ay-form ay-form--v2' : 'ay-form'}
          noValidate
          aria-label={text(template.label)}
          {...(targetMin === undefined ? {} : { 'data-target-min': String(targetMin), style: { '--ay-form-target-min': `${targetMin}px` } as CSSProperties })}
          data-format={format}
          {...(layered ? { 'data-navigation': mode } : {})}
          onSubmit={(event) => {
            event.preventDefault();
            submit();
          }}
        >
          {/* Bandeau invariant (2026-10-19) : toute fiche, en pages ou d'une seule page. */}
          {v2 && formLayout?.header !== undefined ? (
            // Bandeau réglable (1.10.0) : visuel, surtitre, date et heure, opérateur, raccourci « Valider ».
            <FormHeader template={template} settings={headerSettings(template)} sheetDate={sheetDate} timeZone={timeZone ?? undefined} operator={operator} disabled={disabled} {...(preview ? {} : { onExit: () => setExiting(true) })} showSubmit={submitShortcutShown(headerSettings(template), !navigated || lastStep, footerHidden(template))}>
              {bannerBlocks.length > 0 ? bannerBlocks : undefined}
            </FormHeader>
          ) : (
            <FormBanner sheetDate={sheetDate} operator={operator} disabled={disabled} {...(preview ? {} : { onExit: () => setExiting(true) })} validateShortcut={navigated && !lastStep}>
              {paged ? bannerBlocks : undefined}
            </FormBanner>
          )}
          {toast !== null && (
            <div className="ay-missing-toast" role="alert" data-testid="form-missing-toast">
              <span className="ay-missing-toast__badge" aria-hidden="true">
                !
              </span>
              <span className="ay-missing-toast__text">{toast.text}</span>
              <button
                type="button"
                className="ay-missing-toast__see"
                onClick={() => {
                  const target = toast.path;
                  setToast(null);
                  goTo(target);
                }}
              >
                Voir
              </button>
              <button type="button" className="ay-missing-toast__close" aria-label="Fermer le message" onClick={() => setToast(null)}>
                ×
              </button>
            </div>
          )}
          {paged ? body : template.sections.filter((section) => reveal || evaluateFormCondition(section.visible_when, answers)).map(placedSection)}
          {!stagedView && allWarnings.length > 0 && (
            <div className="ay-form__warnings" role="status" aria-label="Avertissements">
              <strong>{allWarnings.length > 1 ? `${allWarnings.length} avertissements` : '1 avertissement'} — la fiche peut être validée</strong>
              <ul>
                {allWarnings.map((warning) => (
                  <li key={`${warning.field}:${warning.code}:${warning.message}`}>{warning.message}</li>
                ))}
              </ul>
            </div>
          )}
          {!footerHidden(template) && <FormActionBar
            position={navigated ? (compact ? `${pageIndex + 1}/${pages.length}` : `Étape ${pageIndex + 1} sur ${pages.length} · ${pageLabel(page)}`) : ''}
            compact={compact}
            onPrevious={navigated && pageIndex > 0 ? previousPage : undefined}
            onNext={navigated && !lastStep ? nextPage : undefined}
            disabled={disabled}
            center={actionCenter}
          />}
          {exiting && (
            <ExitDialog
              onCancel={() => {
                proceed.current = null;
                setExiting(false);
              }}
              onSaveAndLeave={() => {
                setExiting(false);
                const origins = keptOrigins(entry.origins, answers);
                const marked = Object.keys(origins).length === 0 ? {} : { origins };
                if (draftKey !== undefined) draftStore?.save(draftKey, { saved_at: new Date().toISOString(), values: { answers, evidence, ...(Object.keys(origins).length === 0 ? {} : { answer_origins: origins }), ...(pageId === undefined ? {} : { page_id: pageId }) }, lines: [] });
                if (onSaveDraft === undefined) leave();
                else void onSaveDraft({ answers, evidence, ...marked, ...(pageId === undefined ? {} : { pageId }) }).then((saved) => saved && leave());
              }}
              onLeave={() => {
                setExiting(false);
                if (draftKey !== undefined) draftStore?.clear(draftKey);
                leave();
              }}
            />
          )}
          {entry.messages.length > 0 && (
            <div className="ay-form__reactions" role="status" aria-live="polite">
              {entry.messages.map((message, index) => (
                <p key={`${message.reaction_id}-${index}`} className={`ay-form__reaction ay-form__reaction--${message.kind}`}>
                  {reactionText(message)}
                </p>
              ))}
            </div>
          )}
          {entry.confirm !== undefined && (
            <ConfirmationDialog
              title={text(entry.confirm.label, 'fr', 'Confirmer ?')}
              message={reactionText(entry.confirm)}
              confirmLabel={text(entry.confirm.confirm_label, 'fr', 'Continuer')}
              cancelLabel={text(entry.confirm.cancel_label, 'fr', 'Annuler')}
              onCancel={() => {
                // « Annuler » rend l'état d'avant : la valeur précédente, la ligne retirée, ou la même page.
                afterConfirm.current = null;
                setEntry((current) => ({ answers: current.cancel?.answers ?? current.answers, origins: current.cancel?.origins ?? current.origins, messages: [] }));
              }}
              onConfirm={() => {
                setEntry((current) => ({ answers: current.answers, origins: current.origins, messages: current.messages }));
                const then = afterConfirm.current;
                if (then !== null) setTimeout(then, 0);
              }}
            />
          )}
          {pending !== null && (
            <NonconformityDialog
              deviations={pending.deviations}
              onCorrect={() => setPending(null)}
              onConfirm={(nonconformities) => {
                const { submission } = pending;
                setPending(null);
                send(nonconformities.length === 0 ? submission : { ...submission, nonconformities });
              }}
            />
          )}
          {confirming !== null && confirmation !== undefined && (
            <ConfirmationDialog
              title={text(confirmation.title as Readonly<Record<string, string>>, 'fr', 'Valider ?')}
              message={displayText(confirmation.message, { aggregates: aggregateTextScope(formAggregates(template, confirming.answers)), answers: confirming.answers })}
              confirmLabel={text(confirmation.confirm_label as Readonly<Record<string, string>> | undefined, 'fr', 'Valider')}
              cancelLabel={text(confirmation.cancel_label as Readonly<Record<string, string>> | undefined, 'fr', 'Revenir')}
              onCancel={() => setConfirming(null)}
              onConfirm={() => {
                const submission = confirming;
                setConfirming(null);
                onSubmit(submission);
              }}
            />
          )}
        </form>
      </CivilTimeZoneContext.Provider>
      </CivilTodayContext.Provider>
    </PersonEchoProvider>
    </FormPeopleContext.Provider>
    </FormWorkItemContext.Provider>
    </ConformityRulesContext.Provider>
    </FormReactionsContext.Provider>
  );
}
